import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  alpha,
  useTheme,
  Grid,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import jobseekerapi from '../../../../services/jobseekerApi';

// Types
interface Module {
  _id: string;
  module_title: string;
  module_slug: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface ModuleFormData {
  module_title: string;
  module_slug: string;
}

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const initialFormData: ModuleFormData = {
  module_title: '',
  module_slug: '',
};

const Modules: React.FC = () => {
  const theme = useTheme();

  // State variables
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [currentModuleId, setCurrentModuleId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ModuleFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Fetch modules on component mount and when filters/pagination changes
  useEffect(() => {
    fetchModules();
  }, [page, rowsPerPage, searchQuery]);

  // Function to fetch modules
  const fetchModules = async () => {
    setLoading(true);
    setError(null);

    try {
      // startToken is page number (1-indexed), endToken is number of records per page
      const startToken = page + 1;
      const endToken = rowsPerPage;

      // const response = await jobseekerapi.post('/user/modulesList', {
      //   startToken,
      //   endToken,
      //   search: searchQuery,
      // });

      const response = await jobseekerapi.get('/user/modulesList');

      if (response?.data?.status) {
        setModules(response.data.data || []);
        setTotalRows(response.data.record_count || 0);
      } else {
        setError('Failed to fetch modules');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch modules');
      console.error('Error fetching modules:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Reset search
  const resetFilters = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Generate slug from module name
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Open Add Module Dialog
  const openAddDialog = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setIsEditMode(false);
    setCurrentModuleId(null);
    setFormDialogOpen(true);
  };

  // Open Edit Module Dialog
  const openEditDialog = (module: Module) => {
    setFormData({
      module_title: module.module_title,
      module_slug: module.module_slug,
    });
    setFormErrors({});
    setIsEditMode(true);
    setCurrentModuleId(module._id);
    setFormDialogOpen(true);
  };

  // Close form dialog
  const closeFormDialog = () => {
    setFormDialogOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
    setIsEditMode(false);
    setCurrentModuleId(null);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.module_title.trim()) {
      errors.module_title = 'Module title is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form input change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'module_title') {
      // Auto-generate slug when module title changes (only in add mode)
      if (!isEditMode) {
        setFormData({
          module_title: value,
          module_slug: generateSlug(value),
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle Save Module (Add or Edit)
  const handleSaveModule = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode && currentModuleId) {
        // Update existing module
        const response = await jobseekerapi.post('/user/editModules', {
          id: currentModuleId,
          module_name: formData.module_title,
          module_slug: formData.module_slug,
        });

        if (response?.data?.status) {
          showAlert('Module updated successfully', 'success');
          fetchModules();
          closeFormDialog();
        }
      } else {
        // Create new module
        const response = await jobseekerapi.post('/user/addModules', {
          module_name: formData.module_title,
          module_slug: formData.module_slug,
        });

        if (response?.data?.status) {
          showAlert('Module created successfully', 'success');
          fetchModules();
          closeFormDialog();
        }
      }
    } catch (err: any) {
      showAlert(err?.response?.data?.message || 'Failed to save module', 'error');
      console.error('Error saving module:', err);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (module: Module) => {
    setModuleToDelete(module);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setModuleToDelete(null);
  };

  // Handle delete module
  const handleDeleteModule = async () => {
    if (!moduleToDelete?._id) return;

    try {
      const response = await jobseekerapi.delete(`/user/deleteModules/${moduleToDelete._id}`);

      if (response?.data?.status) {
        showAlert('Module deleted successfully', 'success');
        fetchModules();
        closeDeleteDialog();
      }
    } catch (err: any) {
      showAlert(err?.response?.data?.message || 'Failed to delete module', 'error');
      console.error('Error deleting module:', err);
    }
  };

  // Show alert
  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Modules Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          sx={{
            px: 3,
            boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          Add Module
        </Button>
      </Box>

      {/* Search Filter */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <TextField
          label="Search Modules"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by name or slug"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>
          Reset
        </Button>
      </Paper>

      {/* Modules Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Module Title</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : modules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    {searchQuery ? 'No modules found matching your search' : 'No modules found'}
                  </TableCell>
                </TableRow>
              ) : (
                modules.map((module) => (
                  <TableRow key={module._id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{module.module_title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={module.module_slug}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: theme.palette.text.secondary }}>
                        {module._id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDate(module.updatedAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => openEditDialog(module)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => openDeleteDialog(module)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Module Dialog */}
      <Dialog open={formDialogOpen} onClose={closeFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Module' : 'Add New Module'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Module Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Module Title"
                name="module_title"
                value={formData.module_title}
                onChange={handleFormChange}
                error={!!formErrors.module_title}
                helperText={formErrors.module_title}
                required
                placeholder="e.g., Upload a resume"
              />
            </Grid>

            {/* Module Slug - Only show in add mode or as read-only in edit mode */}
            {!isEditMode && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Module Slug"
                  name="module_slug"
                  value={formData.module_slug}
                  onChange={handleFormChange}
                  placeholder="Auto-generated from module title"
                  helperText="Slug is auto-generated from the module title. You can modify it if needed."
                  InputProps={{
                    sx: { fontFamily: 'monospace' }
                  }}
                />
              </Grid>
            )}

            {isEditMode && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Module Slug"
                  value={formData.module_slug}
                  disabled
                  helperText="Module slug cannot be changed after creation"
                  InputProps={{
                    sx: { fontFamily: 'monospace' }
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFormDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveModule}>
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete the module "{moduleToDelete?.module_title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteModule} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Modules;
